import { apiClient } from '../lib/apiClient';

// API-side shape 정의 (기존 UI 호환)
export type UserWithRelations = {
  id: string;
  name: string;
  email: string;
  member_id: string;
  is_active: boolean;
  join_date: string | null;
  status: string;
  total_vac_days: number | null;
  remaining_vac_days: number | null;
  vacation_accrued_months: number;
  vacation_last_accrual_date: string | null;
  department: { id: string; name: string } | null;
  position: { id: string; name: string } | null;
  role: { id: string; name: string } | null;
};

export type UserUpdatePayload = Partial<{
  name: string;
  role_id: string;
  department_id: string;
  position_id: string;
  is_active: boolean;
  status: string;
  join_date: string | null;
  total_vac_days: number | null;
  remaining_vac_days: number | null;
}>;

export type UserCreatePayload = {
  email: string;
  name: string;
  password: string;
  role_id: string;
  department_id: string;
  position_id: string;
  member_id?: string;
  phone?: string;
  emergency_contact?: string;
  address?: string;
  join_date?: string | null;
  status?: string;
  is_active?: boolean;
  total_vac_days?: number;
  remaining_vac_days?: number;
};

type ListParams = {
  page?: number; // not used server-side yet
  limit?: number; // not used server-side yet
  search?: string;
  departmentId?: string;
  positionId?: string;
  isActive?: boolean;
};

export const usersApi = {
  async getAll(params?: ListParams): Promise<UserWithRelations[]> {
    const { data } = await apiClient.get('/api/users', {
      params: {
        search: params?.search,
        departmentId: params?.departmentId,
        positionId: params?.positionId,
        isActive: params?.isActive,
      }
    });
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to load users');
    // Backend returns {ok: true, data: {users: [...], total: number}}
    return (data.data?.users || data.data || []) as UserWithRelations[];
  },

  async getById(id: string): Promise<UserWithRelations | null> {
    const { data } = await apiClient.get(`/api/users/${id}`);
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to load user');
    return data.data as UserWithRelations;
  },

  async create(payload: UserCreatePayload): Promise<UserWithRelations> {
    const { data } = await apiClient.post('/api/users', payload);
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to create user');
    return data.data as UserWithRelations;
  },

  async update(id: string, updates: UserUpdatePayload): Promise<UserWithRelations> {
    // Accepts DB-like keys: name, role_id, department_id, position_id, is_active, status
    const { data } = await apiClient.patch(`/api/users/${id}`, updates);
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to update user');
    return data.data as UserWithRelations;
  },

  async deactivate(id: string): Promise<void> {
    const { data } = await apiClient.post(`/api/users/${id}/deactivate`);
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to deactivate user');
  },

  async delete(id: string): Promise<void> {
    const { data } = await apiClient.delete(`/api/users/${id}`);
    if (!data?.ok) throw new Error(data?.error?.message || 'Failed to delete user');
  },

  async resetPassword(_userId: string): Promise<void> {
    // Placeholder — implement via backend secure flow if needed
    console.warn('Password reset not implemented on client.');
  },

  async checkEmail(_email: string): Promise<boolean> {
    // Optional: add a backend route to check email availability
    return true;
  },

  async getSupervisors(_departmentId: string, _positionLevel: number): Promise<Array<{ id: string; name: string; email: string }>> {
    // Placeholder — implement on backend when needed
    return [];
  }
};
